import { InfoOutlined } from '@mui/icons-material'
import { Checkbox, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { getGiveLivelyDonationEmbedLink } from '../async/give-lively'
import { HBox, VBox, WhiteBox } from '../elements/basic-elements'
import { ErrorText } from '../elements/form-elements'
import { authState } from '../state/auth-state'
import { GiveLivelyDonationConfig } from '../types/give-lively-types'
import { createGiveLivelyDonationAction } from '../utilities/account'
import { trackSupabaseEvent } from '../async/supabase'
import { getErrorMessage, hasValue } from '../utilities/data'
import PrimaryButton from './buttons/primary-button'
import InputComponent from './input'
import LoadingOverlay from './loading-overlay'
import TooltipTitle from './tooltip-title'

export const GiveLivelyDonationForm = ({
  organization,
  actionId
}: {
  organization: Queries.SanityOrganization
  actionId: string
}) => {
  const [page, setPage] = useState(1)

  const { account } = authState()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const [embedLink, setEmbedLink] = useState<string>()

  const [donationConfig, setDonationConfig] = useState<GiveLivelyDonationConfig>({})

  const initSession = useCallback(
    async ({ amount, isRecurring, isPublic }: GiveLivelyDonationConfig) => {
      setLoading(true)
      try {
        const embedLink = await getGiveLivelyDonationEmbedLink(
          amount,
          organization.ein,
          isRecurring
        )
        setEmbedLink(embedLink)
        setPage(2)
        setDonationConfig({ amount, isRecurring, isPublic })
        setError(undefined)
      } catch (err) {
        setError(getErrorMessage(err, 'An unknown error occured; please try again.'))
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const submitDonation = useCallback(async () => {
    setLoading(true)

    try {
      await createGiveLivelyDonationAction(account, actionId, donationConfig)
      if (account?.id) {
        void trackSupabaseEvent({
          eventType: 'action_taken',
          userId: account.id,
          actionSlug: actionId,
          occurredAt: new Date().toISOString(),
          metadata: { source: 'actiontoolwebapp' }
        }).catch(error => {
          console.warn('Supabase track_event failed', error)
        })
      }
    } catch (err) {
      setError(getErrorMessage(err, 'An unknown error occured; please try again.'))
    } finally {
      setLoading(false)
    }
  }, [donationConfig, account, actionId])

  return (
    <VBox justifyContent="center" gap={2} flex={1} sx={{ pointerEvents: 'all' }}>
      <Typography variant="h4" textAlign="center" color="textPrimary">
        Donate to <br /> {organization.title}
      </Typography>{' '}
      <VBox flex={1}>
        <LoadingOverlay loading={loading} />
        {page === 1 && <FormPageOne organization={organization} onSubmit={initSession} />}
        {page === 2 && (
          <FormPageTwo
            embedLink={embedLink}
            onBack={() => setPage(1)}
            onComplete={submitDonation}
          />
        )}
      </VBox>
      {error && <ErrorText>{error}</ErrorText>}
    </VBox>
  )
}

const FormPageOne = ({
  organization,
  onSubmit
}: {
  organization: Queries.SanityOrganization
  onSubmit: (amount: GiveLivelyDonationConfig) => void
}) => {
  const [amount, setAmount] = useState<number>()
  const [isRecurring, setIsRecurring] = useState<boolean>(false)
  const [isPublic, setIsPublic] = useState<boolean>(false)

  const id = `givelively-donation-${organization.title}`

  const canDonate = hasValue(amount) && amount > 0
  return (
    <VBox gap={1} py={2}>
      <Typography variant="overline" color="textPrimary" lineHeight={1}>
        How much would you like to donate?
      </Typography>
      <InputComponent
        hasValue={canDonate}
        onCloseClick={() => {
          setAmount(undefined)
        }}
        startIcon={amount && <Typography>$</Typography>}
      >
        {({ TextBox }) => {
          return (
            <TextBox
              id={id}
              type="number"
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
              }}
              value={amount || ''}
              aria-label="amount"
              placeholder={'Donation amount (in USD)'}
              onChange={e => {
                setAmount(e.target.value ? Number(e.target.value) : undefined)
              }}
              sx={{
                // remove arrows in Chrome/Edge
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0
                },
                // remove arrows in Firefox
                '& input[type=number]': {
                  '-moz-appearance': 'textfield'
                }
              }}
            />
          )
        }}
      </InputComponent>
      <WhiteBox p={1}>
        <HBox alignItems="center" gap={1}>
          <Checkbox
            size="small"
            color="secondary"
            checked={isRecurring}
            onChange={e => {
              setIsRecurring(e.target.checked)
            }}
          />
          <Typography variant="caption" color="textPrimary" sx={{ ml: -0.25 }}>
            Make donation recurring?
          </Typography>

          <Tooltip
            placement="top"
            title={
              <TooltipTitle
                fontSize={12}
                description={`If checked, you will be given an option of how often to make this donation during the next step. You can cancel the recurring donation at any time.`}
              />
            }
            PopperProps={{
              disablePortal: true
            }}
            sx={{
              display: 'inline-block'
            }}
          >
            <InfoOutlined color="info" fontSize="small" />
          </Tooltip>
        </HBox>
      </WhiteBox>
      <WhiteBox p={1}>
        <HBox alignItems="center" gap={1}>
          <Checkbox
            size="small"
            color="secondary"
            checked={isPublic}
            onChange={e => {
              setIsPublic(e.target.checked)
            }}
          />
          <Typography variant="caption" color="textPrimary" sx={{ ml: -0.25 }}>
            Make donation amount public?
          </Typography>

          <Tooltip
            placement="top"
            title={
              <TooltipTitle
                fontSize={12}
                description={`If checked, the donation amount will be included when viewing this action on your profile.`}
              />
            }
            PopperProps={{
              disablePortal: true
            }}
            sx={{
              display: 'inline-block'
            }}
          >
            <InfoOutlined color="info" fontSize="small" />
          </Tooltip>
        </HBox>
      </WhiteBox>
      <PrimaryButton
        disabled={!canDonate}
        variant="contained"
        onClick={() => onSubmit({ amount, isPublic, isRecurring })}
      >
        Next
      </PrimaryButton>
    </VBox>
  )
}

const FormPageTwo = ({
  embedLink,
  onBack,
  onComplete
}: {
  embedLink?: string
  onBack: () => void
  onComplete: () => void
}) => {
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (embedLink) {
      const glListener = (event: MessageEvent) => {
        if (event.data.message === 'gl_checkout_start') {
          /* data:
				     cartId: String
				     amount: Intger,
				     numberOfItems: Intger,
				     tipAmount: Intger,
				     paymentMethod: Null
		     */
          setLoading(false)
        }
        if (event.data.message === 'gl_checkout_complete') {
          /* data:
				     cartId: String
				     amount: Intger,
				     numberOfItems: Intger,
				     tipAmount: Intger,
				     paymentMethod: "card"|"bank"|"paypal"|"venmo"
		     */
          alert('Done')
          console.log('event.data', event.data)
          onComplete()
        }
      }

      window.addEventListener('message', glListener)

      return () => {
        window.removeEventListener('message', glListener)
      }
    }
  }, [embedLink])

  if (!embedLink) {
    return (
      <VBox gap={2} minHeight="100%">
        <ErrorText>No donation embed found; please go back and try again.</ErrorText>
        <PrimaryButton variant="contained" onClick={onBack}>
          Back
        </PrimaryButton>
        {/* <PrimaryButton variant="contained" onClick={onComplete}>
          Make Donation
        </PrimaryButton> */}
      </VBox>
    )
  }
  return (
    <VBox height="100%" position="relative">
      <LoadingOverlay loading={loading} hideBackground color="dark" />
      <iframe
        src={embedLink}
        style={{ minHeight: '100%', border: 0, background: 'transparent', borderRadius: '16px' }}
      />
    </VBox>
  )
}
